/**
 * Device & PWA Environment Detection Utilities
 * Detects whether the simulation is running in a PWA standalone app,
 * mobile device, or regular desktop website browser.
 */

export function isPwaStandalone(): boolean {
  if (typeof window === 'undefined') return false;

  // 1. Standard W3C display-mode media query (Chrome, Edge, Android, Desktop PWA)
  const isDisplayStandalone = window.matchMedia('(display-mode: standalone)').matches;

  // 2. iOS Safari standalone home-screen WebApp mode
  const isIosStandalone = (window.navigator as any).standalone === true;

  // 3. Android Trusted Web Activity (TWA) or WebAPK launcher
  const isAndroidTwa = typeof document !== 'undefined' && document.referrer.includes('android-app://');

  // 4. Fullscreen display mode (some Android PWAs report display-mode: fullscreen)
  const isDisplayFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;

  return isDisplayStandalone || isIosStandalone || isAndroidTwa || isDisplayFullscreen;
}

export function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent || '';
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  const isTouchMac = typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1 && /Macintosh/i.test(ua);
  const isNarrowScreen = typeof window !== 'undefined' && window.innerWidth <= 860;

  return mobileRegex.test(ua) || isTouchMac || isNarrowScreen;
}

/**
 * Detects whether the app is running in an embedded WebView,
 * In-App Browser (Facebook, Instagram, LinkedIn, Slack, Teams),
 * or native container (Capacitor, Cordova, React Native, Electron webview).
 */
export function isWebView(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent || '';

  // 1. Android WebView typically contains '; wv)' or 'Version/X.X Chrome'
  const isAndroidWebView = /Android.*(wv|\.0\.0\.0)/i.test(ua) || (/(Android|Linux)/i.test(ua) && /Version\/[0-9.]+/i.test(ua) && /Chrome\/[0-9.]+/i.test(ua));

  // 2. iOS In-App Browsers / WebViews or WKWebView lacking Safari navigation UI
  const isIos = /iPhone|iPad|iPod/i.test(ua);
  const isIosWebView = isIos && (!/Safari/i.test(ua) || /FBAN|FBAV|Instagram|LinkedInApp|Twitter|Snapchat|musical_ly|ByteLocale/i.test(ua));

  // 3. Native container bridges (Capacitor, Cordova, React Native WebView, WebKit message handlers)
  const isContainerBridge = Boolean(
    (window as any).ReactNativeWebView ||
    (window as any).Capacitor ||
    (window as any).cordova ||
    (window as any).webkit?.messageHandlers ||
    (window as any).AndroidBridge
  );

  return isAndroidWebView || isIosWebView || isContainerBridge;
}

export function shouldUsePwaSimulation(): boolean {
  if (typeof window === 'undefined') return false;

  // Explicit URL query param override: ?mode=pwa or ?mode=web
  const urlParams = new URLSearchParams(window.location.search);
  const modeParam = urlParams.get('mode');
  if (modeParam === 'pwa') return true;
  if (modeParam === 'web') return false;

  // If in PWA standalone mode OR in a WebView OR on a mobile device/viewport, use PWA-optimized simulation
  return isPwaStandalone() || isWebView() || isMobileDevice();
}

